export type PromptKey =
  | 'READY'
  | 'BLINK'
  | 'TURN_LEFT'
  | 'TURN_RIGHT'
  | 'RETURN_CENTER'
  | 'LIVENESS_PASSED'
  | 'READ_COMMITMENT'
  | 'MINIMUM_REACHED'
  | 'SUCCESS'
  | 'RETRY';

export interface PromptDefinition {
  file: string;
  text: string;
}

export const PROMPT_DEFINITIONS: Record<PromptKey, PromptDefinition> = {
  READY: { file: 'ready.mp3', text: '请将面部正对屏幕，并保持在识别框内。' },
  BLINK: { file: 'blink.mp3', text: '请眨眼。' },
  TURN_LEFT: { file: 'turn-left.mp3', text: '请向左转头。' },
  TURN_RIGHT: { file: 'turn-right.mp3', text: '请向右转头。' },
  RETURN_CENTER: { file: 'return-center.mp3', text: '很好，请回正。' },
  LIVENESS_PASSED: { file: 'liveness-passed.mp3', text: '动作检测完成。' },
  READ_COMMITMENT: { file: 'read-commitment.mp3', text: '请看向镜头，完整朗读屏幕上的承诺内容。' },
  MINIMUM_REACHED: { file: 'minimum-reached.mp3', text: '已达到最短录制时长，可以结束录制。' },
  SUCCESS: { file: 'success.mp3', text: '核验完成。' },
  RETRY: { file: 'retry.mp3', text: '检测失败，请按屏幕提示重新操作。' },
};

export class AudioPrompt {
  private enabled = true;
  private audio: HTMLAudioElement | null = null;
  private lastKey: PromptKey | null = null;
  private lastPlayedAt = 0;
  private generation = 0;

  get isEnabled(): boolean {
    return this.enabled;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) this.stop();
  }

  unlock(): void {
    // iOS/微信要求声音能力由用户手势触发；启动按钮点击时恢复语音合成上下文。
    window.speechSynthesis?.resume();
  }

  async play(key: PromptKey, force = false): Promise<void> {
    if (!this.enabled || document.hidden) return;
    const now = performance.now();
    // Face Landmarker 每秒回调多次，相同状态短时间内只允许播报一次。
    if (!force && this.lastKey === key && now - this.lastPlayedAt < 1800) return;
    this.lastKey = key;
    this.lastPlayedAt = now;
    this.stop();

    const prompt = PROMPT_DEFINITIONS[key];
    // 复用由启动按钮首次播放的元素，提高移动端后续异步动作提示的播放兼容性。
    const audio = this.audio ?? new Audio();
    audio.src = `/audio/${prompt.file}`;
    audio.preload = 'auto';
    this.audio = audio;
    const generation = this.generation;
    try {
      await audio.play();
    } catch {
      // 静音、重置或下一条提示会使旧 play() 拒绝；旧请求不能再回退朗读，避免取消后仍然出声。
      if (generation !== this.generation || !this.enabled || document.hidden) return;
      // 录音文件尚未提供、格式不支持或自动播放受限时，回退到系统中文 TTS。
      this.speak(prompt.text);
    }
  }

  stop(): void {
    this.generation += 1;
    if (this.audio) {
      this.audio.pause();
      this.audio.removeAttribute('src');
      this.audio.load();
    }
    window.speechSynthesis?.cancel();
  }

  private speak(text: string): void {
    if (!('speechSynthesis' in window) || typeof SpeechSynthesisUtterance === 'undefined') return;
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'zh-CN';
    utterance.rate = 0.95;
    utterance.pitch = 1;
    utterance.volume = 1;
    window.speechSynthesis.speak(utterance);
  }
}
